import {useCallback, useEffect, useRef, useState} from 'react';

import {AdcConjugationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ConjugationSite} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {serializeConjugationSites} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteSerialization';
import {getConjugationMethodFromTypedValue} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import {AntibodyDomain} from 'molbio/adcs/conjugation-sites/antibodyDomainsQuery';
import {
  ConjugationSiteCandidatesResult,
  findConjugationSiteCandidates,
} from 'molbio/adcs/conjugation-sites/findConjugationSiteCandidates';
import useAntibodyDomains from 'molbio/adcs/conjugation-sites/useAntibodyDomains';
import {StructuredTableColumnGroups} from 'notebook/structured-tables/StructuredTableConstants';
import {
  findColumnIndexByGroup,
  getBioentityIdFromEvaluatedCell,
} from 'notebook/tables/search-scoping/rowScopedSearch';
import {RowModelContext} from 'notebook/tables/store/contexts/RowModelContext';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';
import {useTableMutations} from 'notebook/tables/store/hooks/useTableMutations';

export interface AdcConjugationSitesCellState {
  /** The row's protein, or null if it is empty or invalid. */
  antibodyId: string | null;
  /** The row's conjugation method, or null if it is empty or invalid. */
  conjugationMethod: AdcConjugationMethod | null;
  /** The candidate sites for the row's protein and method, or null until they are found. */
  candidates: ConjugationSiteCandidatesResult | null;
  /** The row's antibody's domains for labeling sites, or null while they load or if they can't be read. */
  domains: Array<AntibodyDomain> | null;
  /** Commits a new selection of sites through the cell's `onUpdate`. */
  updateSites: (newSites: Array<ConjugationSite>) => void;
}

interface FoundCandidates {
  antibodyId: string;
  conjugationMethod: AdcConjugationMethod;
  result: ConjugationSiteCandidatesResult;
}

/**
 * Manages a Conjugation sites cell in an ADC registration table: finds the candidate sites for the row's
 * Protein and Conjugation method, loads the protein's domains to label sites, commits the sites the user chooses, and clears the selection when the method
 * changes to a different method. An empty method is not treated as a change, so the cell keeps its sites while the method
 * is still being evaluated. Only changes made while the cell is rendered are seen; the column's custom eval
 * flags sites left over from any other change.
 *
 * Clearing writes the cell directly rather than through `onUpdate`, the cell's selector handler, which would
 * also select it. Must be called inside a table row (see RowModelProvider).
 *
 * @param sites - the cell's selected sites
 * @param onUpdate - commits the cell's new text when the user changes the selection
 */
export default function useAdcConjugationSitesCell(
  sites: Array<ConjugationSite>,
  onUpdate: (text: string) => void
): AdcConjugationSitesCellState {
  const {rowIndex} = RowModelContext.useContext();
  const {updateCell} = useTableMutations();
  const columnIndex = useRowModel(({columnConstraints}) =>
    findColumnIndexByGroup(columnConstraints, StructuredTableColumnGroups.ADC_CONJUGATION_SITES)
  );
  const antibodyId = useRowModel(({columnConstraints, evaluatedRow}) =>
    getBioentityIdFromEvaluatedCell(
      evaluatedRow[findColumnIndexByGroup(columnConstraints, StructuredTableColumnGroups.ADC_PROTEIN)]
    )
  );
  const domains = useAntibodyDomains(antibodyId);
  const conjugationMethod = useRowModel(({columnConstraints, evaluatedRow}) =>
    getConjugationMethodFromTypedValue(
      evaluatedRow[
        findColumnIndexByGroup(columnConstraints, StructuredTableColumnGroups.ADC_CONJUGATION_METHOD)
      ]?.value
    )
  );

  const [found, setFound] = useState<FoundCandidates | null>(null);
  useEffect(() => {
    if (antibodyId == null || conjugationMethod == null) {
      return undefined;
    }
    let isCurrent = true;
    findConjugationSiteCandidates({antibodyId, conjugationMethod}).then((result) => {
      if (isCurrent) {
        setFound({antibodyId, conjugationMethod, result});
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [antibodyId, conjugationMethod]);
  // Candidates found for earlier inputs are never returned.
  const candidates =
    found != null && found.antibodyId === antibodyId && found.conjugationMethod === conjugationMethod
      ? found.result
      : null;

  const sitesText = serializeConjugationSites(sites);
  const lastMethodRef = useRef(conjugationMethod);
  useEffect(() => {
    if (conjugationMethod == null || conjugationMethod === lastMethodRef.current) {
      return;
    }
    const lastMethod = lastMethodRef.current;
    lastMethodRef.current = conjugationMethod;
    if (lastMethod != null && sitesText !== '') {
      updateCell([rowIndex, columnIndex], {text: ''});
    }
  }, [conjugationMethod, sitesText, updateCell, rowIndex, columnIndex]);

  const updateSites = useCallback(
    (newSites: Array<ConjugationSite>) => {
      onUpdate(serializeConjugationSites(newSites));
    },
    [onUpdate]
  );

  return {antibodyId, conjugationMethod, candidates, domains, updateSites};
}
